import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InventoryIcon from '@mui/icons-material/Inventory';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React from 'react';

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  }).format(new Date(dateString));
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 160 }}
    >
      {label}
    </Typography>
    <Typography variant="body2">{value ?? 'N/A'}</Typography>
  </Box>
);

export default function PurchaseOrderEntriesTab({ poRecords }) {
  if (!poRecords || poRecords.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No purchase order entries found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No entries were returned from the Zim OData API for this PO Number
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {poRecords.map((po) => {
        const entries = po.PurchaseOrderEntries ?? [];

        return (
          <Box key={po.ID} sx={{ mb: 4 }}>
            {/* PO-level summary */}
            <Paper
              variant="outlined"
              sx={{ p: 2, mb: 2, borderRadius: 2, backgroundColor: '#1E2235' }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                PO #{po.PONumber} — {po.POTitle}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: '1fr 1fr 1fr'
                  },
                  gap: 0.5
                }}
              >
                <DetailRow label="ID" value={po.ID} />
                <DetailRow label="Status" value={po.Status} />
                <DetailRow label="PO Type" value={po.POType} />
                <DetailRow
                  label="Date Created"
                  value={formatDateTime(po.DateCreated)}
                />
                <DetailRow
                  label="Date Placed"
                  value={formatDateTime(po.DatePlaced)}
                />
                <DetailRow
                  label="Required Date"
                  value={formatDateTime(po.RequiredDate)}
                />
                <DetailRow
                  label="Last Updated"
                  value={formatDateTime(po.LastUpdated)}
                />
                <DetailRow label="Supplier ID" value={po.SupplierID} />
                <DetailRow label="Batch Number" value={po.BatchNumber} />
                <DetailRow
                  label="Is Placed"
                  value={po.IsPlaced ? 'Yes' : 'No'}
                />
                <DetailRow
                  label="Shipping"
                  value={formatCurrency(po.Shipping)}
                />
                <DetailRow
                  label="Est. Shipping"
                  value={formatCurrency(po.EstShipping)}
                />
                <DetailRow
                  label="Tax Rate"
                  value={po.TaxRate ? `${po.TaxRate}%` : '0%'}
                />
                <DetailRow label="Exchange Rate" value={po.ExchangeRate} />
                <DetailRow label="Remarks" value={po.Remarks} />
                <DetailRow label="To" value={po.To} />
              </Box>
            </Paper>

            {/* Entries */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1, ml: 0.5 }}
            >
              Entries ({entries.length})
            </Typography>

            {entries.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                No entries for this PO.
              </Typography>
            ) : (
              entries.map((entry) => {
                const details = entry.PurchaseOrderEntryDetails ?? [];
                const item = entry.Item;

                return (
                  <Accordion
                    key={entry.ID}
                    sx={{
                      mb: 1,
                      borderRadius: '8px !important',
                      '&:before': { display: 'none' }
                    }}
                    elevation={2}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          flexWrap: 'wrap'
                        }}
                      >
                        <InventoryIcon fontSize="small" color="action" />
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {entry.ItemDescription}
                        </Typography>
                        <Chip
                          label={`Entry #${entry.ID}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`Ordered: ${entry.QuantityOrdered}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={`Received: ${entry.QuantityReceivedToDate}`}
                          size="small"
                          color={
                            entry.QuantityReceivedToDate >=
                            entry.QuantityOrdered
                              ? 'success'
                              : 'warning'
                          }
                        />
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ pt: 0 }}>
                      {/* Entry details */}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: 0.5,
                          mb: 2
                        }}
                      >
                        <DetailRow label="Entry ID" value={entry.ID} />
                        <DetailRow label="Item ID" value={entry.ItemID} />
                        <DetailRow
                          label="PO ID"
                          value={entry.PurchaseOrderID}
                        />
                        <DetailRow
                          label="Qty Ordered"
                          value={entry.QuantityOrdered}
                        />
                        <DetailRow
                          label="Qty Received"
                          value={entry.QuantityReceived}
                        />
                        <DetailRow
                          label="Qty Received to Date"
                          value={entry.QuantityReceivedToDate}
                        />
                        <DetailRow
                          label="Last Qty Received"
                          value={entry.LastQuantityReceived}
                        />
                        <DetailRow
                          label="Last Received Date"
                          value={formatDateTime(entry.LastReceivedDate)}
                        />
                        <DetailRow
                          label="Price"
                          value={formatCurrency(entry.Price)}
                        />
                        <DetailRow
                          label="Tax Rate"
                          value={entry.TaxRate ? `${entry.TaxRate}%` : '0%'}
                        />
                        <DetailRow
                          label="Shipping / Item"
                          value={formatCurrency(entry.ShippingPerItem)}
                        />
                        <DetailRow
                          label="Other Fees / Item"
                          value={formatCurrency(entry.OtherFeesPerItem)}
                        />
                        <DetailRow
                          label="Order Number"
                          value={entry.OrderNumber || 'N/A'}
                        />
                        <DetailRow
                          label="Last Updated"
                          value={formatDateTime(entry.LastUpdated)}
                        />
                      </Box>

                      {/* Serial numbers table */}
                      {details.length > 0 && (
                        <>
                          <Divider sx={{ mb: 1.5 }} />
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            Serial Numbers ({details.length})
                          </Typography>
                          <TableContainer
                            component={Paper}
                            variant="outlined"
                            sx={{ borderRadius: 1 }}
                          >
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ backgroundColor: '#1E2235' }}>
                                  <TableCell sx={{ fontWeight: 700 }}>
                                    Detail ID
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>
                                    Serial Number
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>
                                    Serial #2
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>
                                    Serial #3
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>
                                    Serial ID
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>
                                    Status
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>
                                    Date
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {details.map((detail) => (
                                  <TableRow
                                    key={detail.ID}
                                    sx={{ '&:last-child td': { border: 0 } }}
                                  >
                                    <TableCell>{detail.ID}</TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {detail.SerialNumber1 || '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {detail.SerialNumber2 || '—'}
                                    </TableCell>
                                    <TableCell>
                                      {detail.SerialNumber3 || '—'}
                                    </TableCell>
                                    <TableCell>{detail.SerialID}</TableCell>
                                    <TableCell>{detail.Status}</TableCell>
                                    <TableCell>
                                      {formatDateTime(detail.Date)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      )}

                      {/* Item details */}
                      {item && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            Item Details
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr',
                                sm: '1fr 1fr',
                                md: '1fr 1fr 1fr'
                              },
                              gap: 0.5
                            }}
                          >
                            <DetailRow label="Item ID" value={item.ID} />
                            <DetailRow
                              label="Description"
                              value={item.Description}
                            />
                            <DetailRow
                              label="Item Lookup Code"
                              value={item.ItemLookupCode}
                            />
                            <DetailRow
                              label="Item Type"
                              value={item.ItemType}
                            />
                            <DetailRow label="Quantity" value={item.Quantity} />
                            <DetailRow
                              label="Price"
                              value={formatCurrency(item.Price)}
                            />
                            <DetailRow
                              label="Cost"
                              value={formatCurrency(item.Cost)}
                            />
                            <DetailRow
                              label="MSRP"
                              value={formatCurrency(item.MSRP)}
                            />
                            <DetailRow
                              label="Sale Price"
                              value={formatCurrency(item.SalePrice)}
                            />
                            <DetailRow
                              label="Last Cost"
                              value={formatCurrency(item.LastCost)}
                            />
                            <DetailRow
                              label="Replacement Cost"
                              value={formatCurrency(item.ReplacementCost)}
                            />
                            <DetailRow
                              label="Reorder Point"
                              value={item.ReorderPoint}
                            />
                            <DetailRow
                              label="Restock Level"
                              value={item.RestockLevel}
                            />
                            <DetailRow
                              label="Barcode Format"
                              value={item.BarcodeFormat}
                            />
                            <DetailRow
                              label="Sub Description 2"
                              value={item.SubDescription2}
                            />
                            <DetailRow
                              label="Last Received"
                              value={formatDateTime(item.LastReceived)}
                            />
                            <DetailRow
                              label="Last Sold"
                              value={formatDateTime(item.LastSold)}
                            />
                            <DetailRow
                              label="Date Created"
                              value={formatDateTime(item.DateCreated)}
                            />
                            <DetailRow
                              label="Last Updated"
                              value={formatDateTime(item.LastUpdated)}
                            />
                            <DetailRow
                              label="Inactive"
                              value={item.Inactive ? 'Yes' : 'No'}
                            />
                            <DetailRow
                              label="Web Item"
                              value={item.WebItem ? 'Yes' : 'No'}
                            />
                            <DetailRow
                              label="Taxable"
                              value={item.Taxable ? 'Yes' : 'No'}
                            />
                            <DetailRow
                              label="Do Not Order"
                              value={item.DoNotOrder ? 'Yes' : 'No'}
                            />
                          </Box>
                        </>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </Box>
        );
      })}
    </Box>
  );
}
